(function () {
  // Exposed so assets/collection-controls.js can re-wire cards injected by a sort re-render
  // (a fresh fetch response has none of the listeners this file attaches) without re-running
  // this whole file or duplicating its logic — same root-scoped init shape either caller needs,
  // just called once at parse time here and again per swap over there.
  window.CowalkiesCatalog = window.CowalkiesCatalog || {};
  window.CowalkiesCatalog.initCards = initCards;
  window.CowalkiesCatalog.collapseCard = collapseCard;

  initCards(document);

  // Storefront strings are rendered per-locale into window.aiStrings by layout/theme.liquid
  // (from locales/*.json, ai.card.*); the English fallback only covers a page that lacks it.
  function aiString(key, fallback) {
    return (window.aiStrings && window.aiStrings[key]) || fallback;
  }

  function initCards(root) {
    var cards = root.querySelectorAll('[data-card-product]');
    if (!cards.length) return;

    cards.forEach(function (card) {
      // This file's own <script> tag can legitimately appear more than once on the same page now
      // (e.g. the catalog grid and a homepage carousel both load it, or a merchant adds the
      // carousel section twice) — each tag re-runs this whole file, including the parse-time
      // initCards(document) call below. Guard against wiring the same card's listeners twice,
      // which would otherwise double-fire everything from swatch clicks to add-to-cart.
      if (card.dataset.cardInitialized) return;
      card.dataset.cardInitialized = 'true';

      setUpMediaDots(card);
      setUpCardNavigation(card);

      // Delegated: catches the notify-me form's submit regardless of whether it's the logged-in
      // one-click version or the logged-out email version (see snippets/product-notify-me.liquid).
      card.addEventListener('submit', function (e) {
        if (!e.target.closest('[data-notify-wrap]')) return;
        e.preventDefault();
        e.stopPropagation();
        submitNotifyForm(e.target);
      });

      // The error message stays up until the shopper actually does something about it — tapping
      // back into the email field (focusin, which bubbles unlike plain focus) or editing it
      // (input) — rather than silently clearing itself on a timer or lingering forever with no
      // way to dismiss it short of submitting again.
      card.addEventListener('focusin', function (e) {
        var emailInput = e.target.closest('[data-notify-email]');
        if (emailInput) clearNotifyError(emailInput.closest('[data-notify-wrap]'));
      });
      card.addEventListener('input', function (e) {
        var emailInput = e.target.closest('[data-notify-email]');
        if (emailInput) clearNotifyError(emailInput.closest('[data-notify-wrap]'));
      });

      var variantsScript = card.querySelector('[data-card-variants]');
      var buyButton = card.querySelector('[data-card-buy]');
      var hasVariants = card.getAttribute('data-has-variants') === 'true';

      if (variantsScript && buyButton) {
        // Try/catch, not a bare call: this runs inside cards.forEach, itself called from the
        // parse-time initCards(document) at the TOP of this file's top-level execution — an
        // uncaught throw here doesn't just skip this one card, it aborts the REST of this
        // script's top-level code too, including everything declared after this point (that bit
        // Cowalkies for real: NOTIFY_REVERT_MS below went permanently undefined for the whole
        // page whenever any one product had a variant with no image, because
        // snippets/product-card.liquid's variants_json rendered a literal Liquid error into that
        // one card's JSON — see that snippet's own comment on the actual fix; this is the
        // defense-in-depth half, for whatever the next version of "one card's data is bad" turns
        // out to be).
        try {
          setUpVariantPicker(card, JSON.parse(variantsScript.textContent), buyButton);
        } catch (err) {
          console.warn('[product-card] could not parse variants JSON for this card — its swatch picker will not work', err);
        }
      }

      if (buyButton) {
        buyButton.addEventListener('click', function (e) {
          e.stopPropagation();
          if (buyButton.disabled || buyButton.classList.contains('is-loading')) return;

          // Same two-step logic everywhere this card renders: a product with real choices opens
          // the picker first rather than adding whatever the default combination happens to be —
          // the second click, once the picker is already open, is what actually adds to cart.
          if (hasVariants && !card.classList.contains('is-options')) {
            // is-expanded is kept alongside is-options as the card's generic "open" marker
            // (collapseOtherCards / collapseCard key off it).
            card.classList.add('is-options', 'is-expanded');
            card.setAttribute('aria-expanded', 'true');
            applyFit(card);
            replayDividerLine(card);
            collapseOtherCards(card);
            return;
          }

          addToCart(buyButton);
        });
      }
    });
  }

  // Only one card's options panel is open at a time — opening one closes any other (matters on
  // the home carousel, where several cards sit side by side).
  function collapseOtherCards(exceptCard) {
    document.querySelectorAll('[data-card-product].is-expanded').forEach(function (other) {
      if (other !== exceptCard) collapseCard(other);
    });
  }

  // Tapping anywhere outside an open card, or pressing Escape, folds its options panel back —
  // tapping the card body itself now navigates to the product page, so it can't double as "close".
  document.addEventListener('click', function (e) {
    document.querySelectorAll('[data-card-product].is-expanded').forEach(function (open) {
      if (!open.contains(e.target)) collapseCard(open);
    });
  });
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    document.querySelectorAll('[data-card-product].is-expanded').forEach(collapseCard);
  });

  // A shopper who already gave their dog's measurements (assets/size-fit.js, same data as the product
  // page's size finder) gets the matching size preselected when the picker opens, and that chip is
  // marked with a dot. Computed at open time so it always reflects the latest saved measurements.
  function applyFit(card) {
    var Fit = window.CowalkiesSizeFit;
    var script = card.querySelector('[data-card-fit]');
    if (!Fit || !script || typeof card.selectOptionValue !== 'function') return;
    var measurements = Fit.read();
    if (!measurements) return;
    var data;
    try {
      data = JSON.parse(script.textContent);
    } catch (err) {
      return;
    }
    if (typeof data.ranges === 'string') {
      try {
        data.ranges = JSON.parse(data.ranges);
      } catch (err) {
        return;
      }
    }
    var result = Fit.recommend(data.ranges, data.rule, measurements);
    if (!result) return;
    var wanted = result.size.toLowerCase();
    var matched = null;
    card.querySelectorAll('[data-option-row]:not([data-locked-color]) [data-option-value]').forEach(function (b) {
      var isFit = b.getAttribute('data-option-value').trim().toLowerCase() === wanted;
      b.toggleAttribute('data-fit', isFit);
      if (isFit) matched = b;
    });
    if (matched && !matched.hasAttribute('data-product-url')) {
      card.selectOptionValue(matched.getAttribute('data-option-value'));
    }
  }

  function collapseCard(card) {
    card.classList.remove('is-expanded', 'is-options');
    if (card.hasAttribute('aria-expanded')) card.setAttribute('aria-expanded', 'false');

    // Folding the card is a full reset, not just a visual collapse — a shopper who picked a
    // sold-out size/color while the options panel was open, saw the notify-me form take over the
    // buy row, then folded the card without picking something else, should land back on exactly
    // what the card first showed (the default variant, Buy button included), not stay stuck on
    // whatever combination happened to be selected. See setUpVariantPicker's own
    // resetToDefaultVariant, exposed here rather than duplicated since this function has no
    // access to that closure's `selected`/`refresh` state on its own.
    if (typeof card.resetToDefaultVariant === 'function') card.resetToDefaultVariant();
  }

  // Restarts the "More details" divider's CSS grow-in animation every time the options panel
  // opens, rather than once ever — remove+reflow+re-add is the standard way to force a CSS
  // animation to replay from its first frame (same technique as product-carousel.js's own
  // replayDividerLine).
  function replayDividerLine(card) {
    var line = card.querySelector('[data-divider-line]');
    if (!line) return;
    line.classList.remove('is-growing');
    void line.offsetWidth;
    line.classList.add('is-growing');
  }

  // Tracks which image is most visible inside the track as the shopper swipes — the same job a
  // scroll-snap "page" indicator does natively on iOS, just driven here since the dots live
  // outside the scrolling element.
  function setUpMediaDots(card) {
    var track = card.querySelector('[data-media-track]');
    var dots = card.querySelectorAll('[data-media-dot]');
    if (!track || !dots.length) return;

    var images = track.querySelectorAll('img');

    function setActiveDot(index) {
      dots.forEach(function (dot, i) {
        dot.classList.toggle('is-active', i === index);
      });
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.intersectionRatio > 0.6) {
            var index = Array.prototype.indexOf.call(images, entry.target);
            if (index > -1) setActiveDot(index);
          }
        });
      },
      { root: track, threshold: [0.6] }
    );
    images.forEach(function (img) {
      observer.observe(img);
    });

    // Dots sit inside the card, which has its own click-to-navigate handler — a tap here has to
    // stop short of that, same reasoning as the buy row's own no-toggle controls.
    dots.forEach(function (dot, i) {
      dot.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        var target = images[i];
        if (target) track.scrollTo({ left: target.offsetLeft, behavior: 'smooth' });
      });
    });
  }

  // Whole-card navigation to the product page — everything except an explicit [data-no-toggle]
  // control (buy button, swatches, image dots) sends the shopper straight to the PDP. Not a
  // plain <a> wrapping the card: the buy button's own variant-picker interaction can't live
  // inside a real <a> without browsers treating nested interactive elements inconsistently —
  // see the comment in snippets/product-card.liquid for the full reasoning. Only ever runs when
  // the card was rendered with link_to_product: true (snippets/product-card.liquid) — that param
  // is what controls whether data-product-url exists at all, so no separate check is needed here.
  function setUpCardNavigation(card) {
    var url = card.getAttribute('data-product-url');
    if (!url) return;

    card.addEventListener('click', function (e) {
      if (e.target.closest('[data-no-toggle]')) return;
      window.location.href = url;
    });

    card.addEventListener('keydown', function (e) {
      if (e.target !== card) return;
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        window.location.href = url;
      }
    });
  }

  function setUpVariantPicker(card, variants, buyButton) {
    var optionRows = card.querySelectorAll('[data-option-row]');
    if (!optionRows.length) return;

    // A locked color card (see snippets/product-card.liquid's color_option param) omits that
    // color's own row from the DOM entirely — e.g. a Color+Size product renders only the Size
    // row, whose real option position is 1 (variant.option2), not 0. Reading data-option-index
    // (the option's actual position, set server-side) instead of the row's plain DOM order keeps
    // every variant['option' + n] lookup below pointed at the right option regardless of which
    // rows got skipped.
    function optionIndexOf(row) {
      return parseInt(row.getAttribute('data-option-index'), 10);
    }

    var selected = [];
    optionRows.forEach(function (row) {
      var current = row.querySelector('[data-selected]');
      selected[optionIndexOf(row)] = current ? current.getAttribute('data-option-value') : null;
    });
    // Snapshot of the card's own starting selection (whatever markup rendered with
    // data-selected) — resetToDefaultVariant below restores exactly this, not just "any
    // available variant", so folding the card always lands back on what it first showed.
    var defaultSelected = selected.slice();

    function findVariant() {
      return variants.filter(function (variant) {
        return selected.every(function (value, index) {
          return value == null || variant['option' + (index + 1)] === value;
        });
      })[0];
    }

    // A value is shown as available if there's *some* variant matching it plus whatever is
    // currently selected for every other option — mirrors Shopify's own combined-availability
    // convention, recomputed client-side as the visitor's selection changes.
    function isValueAvailable(optionIndex, value) {
      return variants.some(function (variant) {
        if (variant['option' + (optionIndex + 1)] !== value) return false;
        if (!variant.available) return false;
        return selected.every(function (selectedValue, index) {
          return index === optionIndex || selectedValue == null || variant['option' + (index + 1)] === selectedValue;
        });
      });
    }

    function refresh() {
      optionRows.forEach(function (row) {
        var index = optionIndexOf(row);
        row.querySelectorAll('[data-option-value]').forEach(function (button) {
          var available = isValueAvailable(index, button.getAttribute('data-option-value'));
          button.classList.toggle('is-unavailable', !available);
        });
      });

      var variant = findVariant();
      var buyRow = card.querySelector('[data-card-buy-row]');
      var label = buyButton.querySelector('[data-buy-label]');
      var image = card.querySelector('[data-card-image]');
      var priceEl = card.querySelector('[data-card-price]');

      if (variant && variant.available) {
        buyButton.disabled = false;
        buyButton.setAttribute('data-variant-id', variant.id);
        if (label) label.textContent = aiString('buy', 'Buy');
        if (priceEl) priceEl.textContent = variant.price;
        if (buyRow) buyRow.removeAttribute('data-unavailable');
      } else {
        buyButton.disabled = true;
        if (label) label.textContent = aiString('soldOut', 'Sold out');
        if (buyRow) buyRow.setAttribute('data-unavailable', '');
      }

      // Tracks the resolved variant (available or not) on the buy row regardless of branch —
      // notify-me needs the id/options of the currently-shown SOLD OUT variant, which the
      // available-only branch above never exposed before (buyButton's own data-variant-id is
      // left stale in the else branch on purpose, so it can't be reused for this).
      if (buyRow) {
        if (variant) {
          buyRow.setAttribute('data-current-variant-id', variant.id);
          buyRow.setAttribute('data-current-variant-options', JSON.stringify(selected));
        } else {
          buyRow.removeAttribute('data-current-variant-id');
          buyRow.removeAttribute('data-current-variant-options');
        }
      }

      // A merchant-curated color gallery (data-color-gallery) is the card's own photo set — the
      // variant's native image is a different file, and swapping it in replaced the gallery's
      // first photo with one the product page doesn't show.
      if (variant && variant.image && image && !image.closest('[data-color-gallery]')) {
        image.src = variant.image;
        image.srcset = '';
      }
    }

    function selectValue(row, index, value) {
      selected[index] = value;
      row.querySelectorAll('[data-option-value]').forEach(function (b) {
        b.toggleAttribute('data-selected', b.getAttribute('data-option-value') === value);
      });
      var selectedLabel = row.querySelector('[data-option-selected]');
      if (selectedLabel) selectedLabel.textContent = value;
    }

    // Used by applyFit: pick a value by name in whichever row offers it.
    card.selectOptionValue = function (value) {
      var done = false;
      optionRows.forEach(function (row) {
        if (done) return;
        var index = optionIndexOf(row);
        row.querySelectorAll('[data-option-value]').forEach(function (b) {
          if (!done && b.getAttribute('data-option-value') === value) {
            selectValue(row, index, value);
            done = true;
          }
        });
      });
      if (done) refresh();
      return done;
    };

    // Exposed on the card element (not just this closure) so collapseCard can call it without
    // needing access to this function's own `selected`/`refresh` state — see collapseCard's own
    // comment for why folding the card needs this at all.
    card.resetToDefaultVariant = function () {
      // A stale in-flight swapToLinkedProduct fetch (shopper picked a linked color swatch, then
      // folded the card before that fetch resolved) checks this same attribute to drop its own
      // response — clearing it here makes that guard fire for the reset case too, instead of the
      // late response overwriting what's being reset back to right now.
      card.removeAttribute('data-linked-url');

      optionRows.forEach(function (row) {
        var index = optionIndexOf(row);
        var value = defaultSelected[index];
        selected[index] = value;
        row.querySelectorAll('[data-option-value]').forEach(function (b) {
          b.toggleAttribute('data-selected', b.getAttribute('data-option-value') === value);
        });
        var selectedLabel = row.querySelector('[data-option-selected]');
        if (selectedLabel) selectedLabel.textContent = value;
      });
      refresh();
    };

    optionRows.forEach(function (row) {
      var index = optionIndexOf(row);
      row.querySelectorAll('[data-option-value]').forEach(function (button) {
        button.addEventListener('click', function (e) {
          e.stopPropagation();
          selectValue(row, index, button.getAttribute('data-option-value'));

          var linkedUrl = button.getAttribute('data-product-url');
          if (linkedUrl) {
            swapToLinkedProduct(card, linkedUrl, buyButton);
          } else {
            refresh();
          }
        });
      });
    });

    refresh();
  }

  // Combined-listing color swatch: the selected value belongs to a different linked product
  // (see snippets/product-card.liquid), so there's no local variant to look up — instead fetch
  // that product's own data and swap the card's preview image/price/buy state in place, without
  // navigating. Mirrors the fetch-and-swap pattern assets/product-info.js uses on the PDP itself.
  function swapToLinkedProduct(card, url, buyButton) {
    card.setAttribute('data-linked-url', url);

    var image = card.querySelector('[data-card-image]');
    var priceEl = card.querySelector('[data-card-price]');
    var buyRow = card.querySelector('[data-card-buy-row]');
    var label = buyButton.querySelector('[data-buy-label]');
    var currencyCode = card.getAttribute('data-currency-code');

    fetch(url + '.js')
      .then(function (response) {
        return response.json();
      })
      .then(function (data) {
        // A later swatch click already moved this card on to a different product — drop this
        // now-stale response instead of clobbering that newer selection.
        if (card.getAttribute('data-linked-url') !== url) return;

        var variant = data.variants.filter(function (v) {
          return v.available;
        })[0] || data.variants[0];

        var src = data.featured_image || (data.images && data.images[0]);
        if (image && src) {
          image.src = withWidth(src, 600);
          image.srcset = '';
        }

        if (variant) {
          var formatted = formatMoney(variant.price, currencyCode);
          if (priceEl) priceEl.textContent = formatted;

          if (variant.available) {
            buyButton.disabled = false;
            buyButton.setAttribute('data-variant-id', variant.id);
            if (label) label.textContent = aiString('buy', 'Buy');
            if (buyRow) buyRow.removeAttribute('data-unavailable');
          } else {
            buyButton.disabled = true;
            if (label) label.textContent = aiString('soldOut', 'Sold out');
            if (buyRow) buyRow.setAttribute('data-unavailable', '');
          }
        }

        // Whole-card tap (setUpCardNavigation) and "More details" should follow the shopper to
        // whichever color they just previewed, not the color the page originally rendered.
        card.setAttribute('data-product-url', url);
        var fullDetailsLinks = card.querySelectorAll('a[data-no-toggle]');
        fullDetailsLinks.forEach(function (a) {
          a.href = url;
        });
      })
      .catch(function () {
        // Leave the card showing its last-good state — better than a broken preview.
      });
  }

  function withWidth(src, width) {
    try {
      var url = new URL(src, window.location.origin);
      url.searchParams.set('width', width);
      return url.toString();
    } catch (e) {
      return src;
    }
  }

  function formatMoney(cents, currencyCode) {
    var amount = cents / 100;
    try {
      return new Intl.NumberFormat(document.documentElement.lang || undefined, {
        style: 'currency',
        currency: currencyCode || 'USD',
        currencyDisplay: 'narrowSymbol',
      }).format(amount);
    } catch (e) {
      return amount.toFixed(2);
    }
  }

  // Resolves true when one more unit of this variant still fits under its stock ceiling. A
  // variant with no ceiling (untracked stock / overselling allowed) always resolves true; if the
  // cart can't be read, it also resolves true and leaves the decision to Shopify's own add call.
  function checkStockLimit(button, variantId) {
    var card = button.closest('[data-card-product]');
    var stockScript = card && card.querySelector('script[data-card-stock]');
    var limits = {};
    try {
      limits = stockScript ? JSON.parse(stockScript.textContent) : {};
    } catch (err) {
      return Promise.resolve(true);
    }
    var limit = limits[String(variantId)];
    if (typeof limit !== 'number') return Promise.resolve(true);

    return fetch(routes.cart_url + '.js')
      .then(function (r) {
        return r.json();
      })
      .then(function (cart) {
        var inCart = 0;
        cart.items.forEach(function (item) {
          if (String(item.variant_id) === String(variantId)) inCart += item.quantity;
        });
        return inCart < limit;
      })
      .catch(function () {
        return true;
      });
  }

  function addToCart(button) {
    var variantId = button.getAttribute('data-variant-id');
    var label = button.querySelector('[data-buy-label]');
    var originalText = label.textContent;

    button.classList.remove('is-added', 'is-error');
    button.classList.add('is-loading');
    label.textContent = aiString('adding', 'Adding…');

    // Sync the cart drawer's own contents in the background (so a later, deliberate click on the
    // cart icon shows the right items) and publish cartUpdate so header.js's badge bump / floating
    // toast (the site's one shared add-to-cart confirmation) fires exactly the way it does
    // everywhere else on the site — the PDP's own Add to cart button (assets/product-form.js)
    // follows the same contract independently, since it goes through a real form submit rather
    // than this fetch-based path.
    var cartDrawer = document.querySelector('cart-drawer');
    var config = fetchConfig('javascript');
    var body = { id: variantId, quantity: 1 };
    if (cartDrawer) {
      body.sections = cartDrawer.getSectionsToRender().map(function (s) {
        return s.id;
      });
      body.sections_url = window.location.pathname;
    }
    config.body = JSON.stringify(body);

    // Never let the cart hold more of a variant than is in stock: without this a card's Buy button
    // could be pressed forever, adding one more every time. Products with no tracked stock have
    // no limit (see data-card-stock in snippets/product-card.liquid).
    checkStockLimit(button, variantId)
      .then(function (withinLimit) {
        if (!withinLimit) {
          showResult(button, label, originalText, 'is-error', aiString('maxInCart', 'Max in cart'));
          return null;
        }
        return fetch(routes.cart_add_url, config).then(function (response) {
          return response.json();
        });
      })
      .then(function (data) {
        if (!data) return;
        if (data.status) {
          showResult(button, label, originalText, 'is-error', data.description || data.message || aiString('addError', 'Could not add to cart'));
          return;
        }

        showResult(button, label, originalText, 'is-added', aiString('added', 'Added'));

        if (cartDrawer && data.sections) {
          cartDrawer.renderContents(data);
        }

        return fetch(routes.cart_url + '.js')
          .then(function (r) {
            return r.json();
          })
          .then(function (cart) {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'product-card', cartData: cart });
          });
      })
      .catch(function () {
        showResult(button, label, originalText, 'is-error', aiString('networkError', 'Network error'));
      })
      .finally(function () {
        button.classList.remove('is-loading');
      });
  }

  // The "is-loading -> is-added/is-error -> revert" timing/classes here match
  // assets/product-form.js's own small self-contained copy of this exact same function, used for
  // the PDP's Add to cart button (opt-in via a [data-buy-label] span — see that file). Kept as two
  // independent copies rather than one shared export: the PDP button's feedback shouldn't depend
  // on this file also being loaded on whatever page happens to render <product-form>.
  function showResult(button, label, originalText, stateClass, text) {
    button.classList.add(stateClass);
    label.textContent = text;

    setTimeout(function () {
      button.classList.remove(stateClass);
      label.textContent = originalText;
    }, stateClass === 'is-error' ? 2400 : 1800);
  }

  // Swaps the button's label for Dawn's own loading-spinner markup (rendered into the button by
  // snippets/product-notify-me.liquid, styled by that same file's [data-notify-submit] rules) —
  // same pattern product-form.js already uses for the add-to-cart button, reused here rather than
  // invented fresh. The label itself stays in the DOM (just hidden via the CSS sibling rule) so
  // there's nothing to restore beyond toggling the spinner back off. Kept as its own copy rather
  // than shared with product-notify-me.js's identical function, same reasoning as this file's
  // other notify-me helpers: the card shouldn't depend on the PDP's script also being loaded.
  function setNotifyLoading(submitButton, isLoading) {
    if (!submitButton) return;
    var spinner = submitButton.querySelector('.loading__spinner');
    if (spinner) spinner.classList.toggle('hidden', !isLoading);
  }

  function submitNotifyForm(form) {
    var wrap = form.closest('[data-notify-wrap]');
    var submitButton = form.querySelector('[data-notify-submit]');
    if (submitButton && submitButton.disabled) return;

    if (submitButton) submitButton.disabled = true;
    setNotifyLoading(submitButton, true);
    clearNotifyError(wrap);

    // Sole request now — this form no longer touches Shopify's customer form at all (dropped:
    // duplicated what the Sheet already captures, and its own POST could get silently rejected
    // by hCaptcha off localhost even for a real shopper). back-to-store-module is the one source
    // of truth here; the Shopify customer form is still used, unchanged, by the footer newsletter
    // signup — see snippets/product-notify-me.liquid's top comment.
    fireBackInStockRequest(form, wrap).then(function (ok) {
      if (ok) {
        // Loading state (disabled + spinner) is left ON here on purpose — the form panel is
        // about to cross-fade out under the success panel (see showNotifySuccess), and clearing
        // it first would flash a plain, enabled "email + Notify me" button for that ~400ms
        // transition. showNotifySuccess's own revert timeout clears it once the form panel is
        // actually visible again.
        showNotifySuccess(wrap, form, submitButton);
      } else {
        setNotifyLoading(submitButton, false);
        if (submitButton) submitButton.disabled = false;
        var errorEl = wrap.querySelector('[data-notify-error]');
        if (errorEl) errorEl.hidden = false;
      }
    });
  }

  // The error stays visible until the shopper does something about it — no auto-dismiss timer.
  // "Something about it" means submitting again (submitNotifyForm calls this too, before
  // re-firing the request) or, so it doesn't feel stuck while they're literally retyping the
  // email, tapping back into the field or editing it — see the focusin/input listeners above.
  function clearNotifyError(wrap) {
    if (!wrap) return;
    var errorEl = wrap.querySelector('[data-notify-error]');
    if (errorEl) errorEl.hidden = true;
  }

  // Resolves a Promise<boolean> (true once back-to-store-module's Apps Script endpoint returns
  // HTTP ok) — see submitNotifyForm above for how that outcome drives the form's own
  // success/error state. Pulls the resolved variant id/options off the buy row (set by refresh()
  // above, both for the available and unavailable branches) rather than tracking a second,
  // separate copy of `selected` here — one source of truth for "what's currently shown" per card.
  function fireBackInStockRequest(form, wrap) {
    if (!window.CowalkiesBackInStock || !wrap) return Promise.resolve(false);

    var emailInput = form.querySelector('[name="email"]');
    var email = emailInput ? emailInput.value : '';

    var card = form.closest('[data-card-product]');
    var buyRow = card && card.querySelector('[data-card-buy-row]');
    var buyButton = card && card.querySelector('[data-card-buy]');

    // Falls back to the buy button's server-rendered data-variant-id for a single-variant
    // product, where setUpVariantPicker (and so refresh()) never runs at all.
    var variantId =
      (buyRow && buyRow.getAttribute('data-current-variant-id')) ||
      (buyButton && buyButton.getAttribute('data-variant-id')) ||
      null;

    var names = [];
    try {
      names = JSON.parse((wrap.getAttribute('data-option-names') || '[]'));
    } catch (e) {}

    var values = [];
    var rawOptions = buyRow && buyRow.getAttribute('data-current-variant-options');
    if (rawOptions) {
      try {
        values = JSON.parse(rawOptions);
      } catch (e) {}
    }

    var options = {};
    names.forEach(function (name, i) {
      if (name && values[i] != null) options[name] = values[i];
    });

    return window.CowalkiesBackInStock.notify({ wrap: wrap, email: email, variantId: variantId, options: options });
  }

  // Cross-fades the pre-rendered form/success panels via the wrap's own [data-state] (see
  // snippets/product-notify-me.liquid's CSS) rather than replacing markup — that's what lets the
  // email field come back a few seconds later instead of the success message being a dead end.
  var NOTIFY_REVERT_MS = 3200;

  function showNotifySuccess(wrap, form, submitButton) {
    if (wrap._notifyRevertTimer) clearTimeout(wrap._notifyRevertTimer);

    var check = wrap.querySelector('[data-notify-check]');
    if (check) {
      // Remove-reflow-readd so the fade/stroke-draw replays from frame 0 if this card is
      // notified more than once in a session (same technique as replayDividerLine above).
      check.setAttribute('data-state', 'out');
      void check.offsetWidth;
      check.setAttribute('data-state', 'in');
    }
    wrap.setAttribute('data-state', 'success');

    wrap._notifyRevertTimer = setTimeout(function () {
      wrap.setAttribute('data-state', 'form');
      if (check) check.setAttribute('data-state', 'out');
      var emailInput = form.querySelector('[data-notify-email]');
      if (emailInput) emailInput.value = '';
      setNotifyLoading(submitButton, false);
      if (submitButton) submitButton.disabled = false;
    }, NOTIFY_REVERT_MS);
  }
})();
